import React from "react";

import { useTranslation } from "react-i18next";

import MultiOffersComponent from "@components-shared/MultiOffersComponent";
import ErrorBoundaryWithPropagation from "@components/boundary/ErrorBoundaryWithPropagation";
import { WalletIcon } from "@components/icons/WalletIcon";
import WalletTag from "@components/shared/WalletTag";
import MyBetsAutosellButton from "@features/my-bets/desktop/common/MyBetsAutosellButton";
import MyBetsCashoutButton from "@features/my-bets/desktop/common/MyBetsCashoutButton";
import { MyBetsAutosellConfirmation } from "@features/my-bets/desktop/common/autosell-confirmation";
import { CashoutConfirmation } from "@features/my-bets/desktop/common/cashout";
import { EventType } from "@gp/models";
import MyBetsViewStore from "@state/stores/my-bets/MyBetsViewStore";
import { getCurrentCulture } from "@utils";
import { getTip, getType } from "@utils/offer";
import { shouldShowBetSlipStatus } from "@utils/offer/shouldShowBetSlipStatus";
import { shouldUseSlipStatusInstead } from "@utils/offer/shouldUseSlipStatusInstead";
import classNames from "classnames";
import { observer } from "mobx-react";

import { DisplayDate } from "./components";
import BetItemPaymentDetail from "./components/BetItemPaymentDetail";
import BetOfferEventTime from "./components/BetOfferEventTime";

export default observer(function BetDisplay(props: {
	myBetsViewStore: MyBetsViewStore;
}) {
	const { t } = useTranslation("mybets");

	if (
		props.myBetsViewStore.openedBet == null ||
		props.myBetsViewStore.openedBet.isRemoved
	) {
		return null;
	}

	let betStatus = props.myBetsViewStore.openedBet.betStatus;
	let betStatusClass = `widget--bets__tag tag tag--tny tag--bet tag--bet--${betStatus.abrv}`;

	if (shouldUseSlipStatusInstead(props.myBetsViewStore.openedBet)) {
		betStatus = props.myBetsViewStore.openedBet.betSlipStatus;
		betStatusClass = `widget--bets__tag tag tag--tny tag--slip tag--slip--${props.myBetsViewStore.openedBet.betSlipStatus.abrv}`;
	}

	return (
		<ErrorBoundaryWithPropagation componentName={"Desktop/BetDisplay.tsx"}>
			<div
				className={
					"widget widget--bets widget--bets--" +
					getCurrentCulture() +
					" card--shadowy"
				}
			>
				<div
					className={
						"widget__header widget__header--" + getCurrentCulture()
					}
				>
					<div className="widget__header__title">
						{t<string>("COMMON.MY_BETS_TITLE")}
					</div>
					<a
						href={`/${getCurrentCulture()}/my-bets`}
						className="widget__header__btn btn btn--xsml btn--ghost btn--icon btn--animate-forward u-padd--x--reset"
					>
						<span>{t<string>("mybets:VIEW_OLDER_BETS")}</span>
						<i className="u-icon u-icon--tny u-icon--arrow-right" />
					</a>
				</div>

				<div className="widget__body widget--bets__details">
					<div
						className={classNames("widget--bets__head", [
							`widget--bets__head--${getCurrentCulture()}`,
						])}
					>
						<div className="widget--bets__status">
							<span
								className={betStatusClass}
								title={betStatus?.name}
							>
								{betStatus?.name}

								{props.myBetsViewStore.openedBet.isBonus && (
									<> ({t<string>("COMMON.BONUS")})</>
								)}
							</span>

							{shouldShowBetSlipStatus(
								props.myBetsViewStore.openedBet
							) && (
								<span
									className={
										"widget--bets__tag tag tag--tny tag--slip tag--slip--" +
										props.myBetsViewStore.openedBet
											.betSlipStatus.abrv
									}
								>
									{
										props.myBetsViewStore.openedBet
											.betSlipStatus.name
									}
								</span>
							)}

							<WalletTag
								isWalletManuallyDisabled={
									props.myBetsViewStore.openedBet
										.isWalletManuallyDisabled || false
								}
							/>

							<WalletIcon
								bettingAccountTypeAbrv={
									props.myBetsViewStore.openedBet
										.bettingAccountType?.abrv
								}
								classList="widget--bets__wallet"
								isWalletManuallyDisabled={
									props.myBetsViewStore.openedBet
										.isWalletManuallyDisabled || false
								}
							/>
						</div>
					</div>

					<div className="widget--bets__title u-mar--bottom--xsml">
						{
							props.myBetsViewStore.openedBet
								.formattedBetSystemString
						}
					</div>

					<div className="widget--bets__list">
						{props.myBetsViewStore.openedBet.betSlipOffers.map(
							(o, oIdx) => (
								<div key={oIdx} className="widget--bets__match">
									<div className="widget--bets__subtitle">
										{o.multiOffers &&
											o.multiOffers.length > 0 && (
												<i className="widget--bets__icon u-icon u-icon--sml u-icon--bb--active" />
											)}

										<span className="u-align--v--middle">
											{o.sportData.eventType ==
											EventType.NORMAL
												? `${o.sportData.teamOneName} - ${o.sportData.teamTwoName}`
												: o.sportData.eventName}
										</span>
									</div>

									<BetOfferEventTime
										myBetsViewStore={props.myBetsViewStore}
										bet={o}
										isInDetails={true}
									/>

									<div className="widget--bets__offer">
										<div className="widget--bets__pick">
											<div className="widget--bets__bet">
												{o.sportData.eventType ==
													EventType.NORMAL && (
													<div
														className={classNames(
															"widget--bets__type u-color--text--primary"
														)}
													>
														{getType(o)}
														{!(
															o.multiOffers &&
															o.multiOffers.length
														) && ":"}
													</div>
												)}
												<div className="widget--bets__tip">
													{getTip(o)}
												</div>
											</div>
											<div className="widget--bets__quota">
												{o.value}
											</div>
										</div>

										{o.multiOffers && (
											<MultiOffersComponent
												multiOffers={o.multiOffers}
											/>
										)}
									</div>
								</div>
							)
						)}
					</div>

					<div className="widget--bets__row">
						<div className="widget--bets__col">
							<div className="widget--bets__label">
								{t<string>("mybets:ITEM.INFO_PAYMENT_LABEL")}:
							</div>
							<div className="widget--bets__amount">
								{props.myBetsViewStore.openedBet.displayPayment}
							</div>
						</div>
						<div className="widget--bets__col">
							<BetItemPaymentDetail
								betStatusAbrv={
									props.myBetsViewStore.openedBet.betStatus
										?.abrv || ""
								}
								betSlipStatusAbrv={
									props.myBetsViewStore.openedBet
										.betSlipStatus.abrv || ""
								}
								payout={
									props.myBetsViewStore.openedBet
										.displayPayout
								}
								maxGain={
									props.myBetsViewStore.openedBet
										.displayMaxGain
								}
							/>
						</div>
						<div className="widget--bets__col">
							<div className="widget--bets__label">
								{t<string>(
									"mybets:ITEM.ADDITIONAL_MAX_COEFFICIENT"
								)}
								:
							</div>
							<div className="widget--bets__amount">
								{props.myBetsViewStore.openedBet.maxCoefficient.toFixed(
									2
								)}
							</div>
						</div>
					</div>

					<div className="widget--bets__actions">
						<MyBetsCashoutButton
							bet={props.myBetsViewStore.openedBet}
							openCashoutModal={
								props.myBetsViewStore.cashoutStore
									.openCashoutModal
							}
						/>
						<MyBetsAutosellButton
							bet={props.myBetsViewStore.openedBet}
							openAutosellModal={
								props.myBetsViewStore.cashoutStore
									.openAutosellModal
							}
						/>
					</div>

					<div className="widget--bets__footer">
						<div className="widget--bets__timestamp">
							<DisplayDate
								betDateTimeStamp={
									props.myBetsViewStore.openedBet
										?.dateCreated || ""
								}
							/>
						</div>

						<button
							type="button"
							className="widget--bets__close btn btn--square btn--square--xsml btn--primary--inverted"
							onClick={props.myBetsViewStore.onClose}
						>
							<i className="u-icon u-icon--tny u-icon--close" />
						</button>
					</div>
				</div>

				<CashoutConfirmation
					isOpen={
						props.myBetsViewStore.cashoutStore.isCashoutModalOpen
					}
					cashoutModalData={
						props.myBetsViewStore.cashoutStore.cashoutModalData
					}
					onAccept={props.myBetsViewStore.cashoutStore.cashout}
					onClose={
						props.myBetsViewStore.cashoutStore.closeCashoutModal
					}
					isSubmitting={
						props.myBetsViewStore.cashoutStore
							.isCashoutFormSubmitting
					}
				/>

				<MyBetsAutosellConfirmation
					store={props.myBetsViewStore.cashoutStore}
				/>
			</div>
		</ErrorBoundaryWithPropagation>
	);
});
